/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import throttle from 'lodash/throttle';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Retracked from 'js/app/retracked';

import type { Theme } from '@coursera/cds-core';
import { Grid, useTheme } from '@coursera/cds-core';

import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import GrowthDegreesExperiments from 'bundles/epic/clients/GrowthDegrees';
import MtcUcert404 from 'bundles/premium-certificate/components/MtcUcert404';
import { CourseraMetatags } from 'bundles/seo';
import { GetDegreeLandingPageContentDocument } from 'bundles/xddp/__generated__/contentfulOperations';
import type {
  DegreeLandingPageContentFragment,
  GetDegreeLandingPageContentQuery,
} from 'bundles/xddp/__generated__/contentfulTypes';
import EmbeddedRegForm from 'bundles/xddp/components/expression-of-interest/EmbeddedRegForm';
import DLPBanner from 'bundles/xddp/components/landing/DLPBanner';
import ProgramOverview from 'bundles/xddp/components/landing/ProgramOverview';
import AtAGlanceSection from 'bundles/xddp/components/shared/AtAGlanceSection';
import Container from 'bundles/xddp/components/shared/Container';
import SharedPremiumHeader from 'bundles/xddp/components/shared/SharedPremiumHeader';
import { usePropsFromRouter } from 'bundles/xddp/hooks/usePropsFromRouter';
import type { UserAgentProps } from 'bundles/xddp/types';
import createEoiCopyTest from 'bundles/xddp/utils/createEoiCopyTest';

type PropsFromDataFetcher = {
  pageContent: DegreeLandingPageContentFragment;
};

type PropsToComponent = PropsFromDataFetcher & UserAgentProps;

const styles = {
  pageStyle: css`
    background: none;

    .rc-EoiBranchContainer {
      background: none;
    }
  `,
  gridContainer: (theme: Theme) => css`
    margin-top: ${theme.spacing(32)};
  `,
  eoiContainer: (theme: Theme) => css`
    position: relative;
    z-index: 3;
    top: -130px;

    ${theme.breakpoints.down('sm')} {
      top: 0;
      margin-bottom: 24px;
    }
  `,
};

const dummyFunction = () => {};

const LandingPage: React.FC<PropsToComponent> = ({ pageContent, userAgent }) => {
  const theme = useTheme();
  const { productType } = usePropsFromRouter();

  const [isStickyBarPresent, setIsStickyBarPresent] = useState(false);

  const getShouldShowEoiV2 = useCallback((slug) => {
    const degreesOnEOIv1 = GrowthDegreesExperiments.get('degreesOnEOIv1');
    return !degreesOnEOIv1.includes(slug);
  }, []);

  const scrollListener = useMemo(
    () =>
      throttle(() => {
        const headerBottom = document.querySelector('.header-container')?.getBoundingClientRect()?.bottom;
        if (!headerBottom) return;
        setIsStickyBarPresent(headerBottom < 0);
      }, 100),
    []
  );

  useEffect(() => {
    window.addEventListener('scroll', scrollListener);
  }, [scrollListener]);

  if (!pageContent || !pageContent.slug || !pageContent.partnerInformation) {
    return <MtcUcert404 productType={productType} />;
  }

  const { slug, programName, programShortName, partnerInformation, title, programOverview, expressionOfInterest } =
    pageContent;
  const { partnerName, partnerLogo, bannerImage, primaryColor, secondaryColor } = partnerInformation;

  return (
    <div className="rc-LandingPage" css={styles.pageStyle} data-testid="mtc-ucert-lp">
      <CourseraMetatags
        metaNameAndProperties={{
          title: programName ?? undefined,
          description: `Explore the ${programName} Program offered by ${partnerName}`,
          absoluteImageHref: partnerLogo?.url ?? undefined,
        }}
      />
      {expressionOfInterest ? (
        <SharedPremiumHeader
          id=""
          slug={slug}
          partnerInformation={partnerInformation}
          programName={programName ?? ''}
          navigationElements={[]}
          onRequestInfoClick={dummyFunction}
          onApplyNowClick={dummyFunction}
          isStickyBarPresent={isStickyBarPresent}
          isStickyEoiPresent={isStickyBarPresent}
          showEOIv2={getShouldShowEoiV2(slug)}
          expressionOfInterestContainer={createEoiCopyTest(expressionOfInterest, slug)}
          userAgent={userAgent}
          showEoiForm={dummyFunction}
          currentNavElement={{}}
        />
      ) : (
        <DLPBanner
          userAgent={userAgent}
          title={title}
          partnerLogo={partnerLogo?.url}
          bannerImage={bannerImage?.url}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      )}
      <Container>
        <div css={styles.gridContainer(theme)}>
          <Grid container spacing={16}>
            {Boolean(pageContent.atAGlance) && (
              <Grid item xs={12}>
                <AtAGlanceSection {...pageContent.atAGlance} slug={slug} />
              </Grid>
            )}
            <Grid item md={pageContent.expressionOfInterest ? 12 : 7} sm={12}>
              <ProgramOverview marketingContent={programOverview} />
            </Grid>
            {!pageContent.expressionOfInterest && (
              <Grid item md={5} sm={12}>
                <div css={styles.eoiContainer(theme)}>
                  <EmbeddedRegForm
                    slug={slug}
                    partnerName={partnerName}
                    shortName={programShortName}
                    // using fake id here since this page does not seem to be used and does not use eventing V3 yet
                    trackingProductId="fakeDegreeId"
                  />
                </div>
              </Grid>
            )}
          </Grid>
        </div>
      </Container>
    </div>
  );
};

export const BaseComp = LandingPage;
const LandingPageDataFetcher = () => {
  const { slug, versionParam, isPreview } = usePropsFromRouter();

  const { data: pageContentData } = useQuery<GetDegreeLandingPageContentQuery, { slug: string; versionParam: string }>(
    GetDegreeLandingPageContentDocument,
    {
      variables: { slug, versionParam },
      // Only send a request through the Preview API if the user can preview
      context: getGrowthCmsGraphqlContext(isPreview),
    }
  );

  const pageContent = pageContentData?.landingPageCollection?.items[0] || {};

  const Page = compose<PropsToComponent, PropsFromDataFetcher>(
    Retracked.createTrackedContainer(() => ({
      namespace: {
        app: 'premium_certificate',
        page: 'landing',
      },
    })),
    connectToStores<PropsFromDataFetcher, PropsToComponent>(['ApplicationStore'], ({ ApplicationStore }) => ({
      userAgent: ApplicationStore.getUserAgent(),
    }))
  )(LandingPage);

  return <Page pageContent={pageContent} />;
};

export default LandingPageDataFetcher;
